import React, { useState, useEffect, useCallback } from 'react'
import { Link, useNavigate, useLocation } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'
import { Helmet } from 'react-helmet-async'
import { AppDispatch, RootState } from '@/store/store'
import { loginUser } from '@/store/slices/authSlice'

import LoadingSpinner from '@/components/ui/LoadingSpinner'
import PasswordInput from '@/components/ui/PasswordInput'
import EmailValidation from '@/components/ui/EmailValidation'
import Logo from '@/components/ui/Logo'
import { useDraftSave } from '@/hooks/useDraftSave'

const LoginPage: React.FC = () => {
    const dispatch = useDispatch<AppDispatch>()
    const navigate = useNavigate()
    const location = useLocation()
    const { isAuthenticated, user, token } = useSelector((state: RootState) => state.auth)

    const [formData, setFormData] = useState({
        email: location.state?.email || '',
        password: ''
    })

    const [isLoading, setIsLoading] = useState(false)

    // Memoized restore handler to ensure stable dependency for useDraftSave
    const handleRestoreDraft = useCallback((draft: any) => {
        setFormData({
            email: draft.email || '',
            password: draft.password || ''
        })
    }, [])

    // Initialize draft save utility
    const { draftSave, clearDraft } = useDraftSave({
        pageId: 'login',
        debounceMs: 1000,
        autoRestore: true,
        onRestore: handleRestoreDraft
    })

    const from = location.state?.from?.pathname || '/dashboard'

    // Redirect if already authenticated
    useEffect(() => {
        if (isAuthenticated && user && token) {
            navigate(from, { replace: true })
        }
    }, [isAuthenticated, user, token, navigate, from])

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault()
        setIsLoading(true)

        try {
            await dispatch(loginUser(formData)).unwrap()
            // Clear draft on successful login
            clearDraft()
            navigate(from, { replace: true })
        } catch (error) {
            // Error is handled by the slice and shown via toast
        } finally {
            setIsLoading(false)
        }
    }



    return (
        <>
            <Helmet>
                <title>Sign In - SideGO</title>
                <meta name="description" content="Sign in to your SideGO account to access gigs and opportunities." />
            </Helmet>

            <div className="min-h-screen flex items-center justify-center bg-gray-50 dark:bg-gray-900 py-12 px-4 sm:px-6 lg:px-8">
                <div className="max-w-md w-full space-y-8">
                    <div>
                        <div className="flex justify-center">
                            <Logo
                                size="lg"
                                showText={true}
                                className="justify-center"
                                variant="default"
                            />
                        </div>
                        <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900 dark:text-white">
                            Welcome back
                        </h2>
                        <p className="mt-2 text-center text-sm text-gray-600 dark:text-gray-400">
                            Don't have an account?{' '}
                            <Link
                                to="/register"
                                className="font-medium text-primary-600 hover:text-primary-500"
                            >
                                Sign up for free
                            </Link>
                        </p>
                    </div>

                    <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
                        <div className="space-y-4">
                            {/* Email */}
                            <div>
                                <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Email address
                                </label>
                                <EmailValidation
                                    value={formData.email}
                                    onChange={(value) => {
                                        const newData = { ...formData, email: value }
                                        setFormData(newData)
                                        draftSave(newData)
                                    }}
                                    placeholder="Enter your email address"
                                    showValidation={false}
                                    required
                                    autoComplete="email"
                                />
                            </div>

                            {/* Password */}
                            <div>
                                <label htmlFor="password" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
                                    Password
                                </label>
                                <PasswordInput
                                    value={formData.password}
                                    onChange={(value) => {
                                        const newData = { ...formData, password: value }
                                        setFormData(newData)
                                        draftSave(newData)
                                    }}
                                    placeholder="Enter your password"
                                    name="password"
                                    id="password"
                                    required
                                    autoComplete="current-password"
                                />
                            </div>
                        </div>

                        <div className="flex items-center justify-between">
                            <div className="flex items-center">
                                <input
                                    id="remember-me"
                                    name="remember-me"
                                    type="checkbox"
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label htmlFor="remember-me" className="ml-2 block text-sm text-gray-900 dark:text-gray-300">
                                    Remember me
                                </label>
                            </div>

                            <div className="text-sm">
                                <Link
                                    to="/forgot-password"
                                    className="font-medium text-primary-600 hover:text-primary-500"
                                >
                                    Forgot password?
                                </Link>
                            </div>
                        </div>

                        <div>
                            <button
                                type="submit"
                                disabled={isLoading}
                                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg text-white bg-primary-600 hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {isLoading ? (
                                    <LoadingSpinner size="sm" color="white" />
                                ) : (
                                    'Sign in'
                                )}
                            </button>
                        </div>
                    </form>

                    <div className="mt-6">
                        <div className="relative">
                            <div className="absolute inset-0 flex items-center">
                                <div className="w-full border-t border-gray-300 dark:border-gray-600" />
                            </div>
                            <div className="relative flex justify-center text-sm">
                                <span className="px-2 bg-gray-50 dark:bg-gray-900 text-gray-500 dark:text-gray-400">
                                    New to SideGO?
                                </span>
                            </div>
                        </div>

                        <div className="mt-6 grid grid-cols-2 gap-3">
                            <Link
                                to="/register?role=worker"
                                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Join as Worker
                            </Link>
                            <Link
                                to="/register?role=employer"
                                className="w-full inline-flex justify-center py-2 px-4 border border-gray-300 dark:border-gray-600 rounded-md shadow-sm bg-white dark:bg-gray-800 text-sm font-medium text-gray-500 dark:text-gray-400 hover:bg-gray-50 dark:hover:bg-gray-700"
                            >
                                Join as Employer
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        </>
    )
}

export default LoginPage